import { pluralize } from "@prismicio/editor-support/String"
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Icon,
  Text,
} from "@prismicio/editor-ui"
import { JsonEditor } from "json-edit-react"
import { useMemo } from "react"

import { prismicClientDocsUrl } from "../constants"
import { useCopy } from "../hooks/useCopy"
import {
  type ResponseError,
  type ResponseSuccess,
  useAPIExplorer,
} from "./APIExplorerContext"
import { MissingParameterError } from "./Request/MissingParameterError"

export function APIExplorerResponse() {
  const { response, request } = useAPIExplorer()

  return (
    <Box
      display="grid"
      gridTemplateRows="auto 1fr"
      borderRadius={{ topLeft: 8, topRight: 8 }}
      border={{ top: true, right: true, left: true, bottom: false }}
      minHeight={0}
      minWidth={0}
    >
      <Box
        as="header"
        padding={{ inline: 16 }}
        alignItems="center"
        justifyContent="space-between"
        border={{ bottom: true }}
        minHeight={48}
      >
        <Text variant="small" component="h2" color="grey9">
          Response
        </Text>
        {(response.status === "success" || response.status === "error") && (
          <Button
            startIcon="refresh"
            color="grey"
            invisible
            size="small"
            onClick={() => request.run()}
          >
            Refresh
          </Button>
        )}
      </Box>
      {response.status === "idle" && <APIExplorerResponseIdle />}
      {response.status === "pending" && <APIExplorerResponsePending />}
      {response.status === "error" && (
        <APIExplorerResponseError response={response} />
      )}
      {response.status === "success" && (
        <APIExplorerResponseSuccess response={response} />
      )}
    </Box>
  )
}

function APIExplorerResponseIdle() {
  const { request, config } = useAPIExplorer()

  const runDemoQuery = () => {
    request.setRepositoryID("prismic-main")
    request.setRequestType("getByType")
    request.setRequestOptions("getByType", { type: "page" })
    // Reset other parameters...
    request.setPagination({})
    request.setLang()
    request.setRef()
    // We don't need to "run" the request because it runs as an effect to the request configuration.
  }

  return (
    <Box display="flex" flexDirection="column" overflowY="auto">
      <Box padding={{ inline: 16, block: 32 }} justifyContent="center">
        <Card padding={0} sx={{ width: 498 }}>
          <CardContent padding={32}>
            <Box gap={8} alignItems="center">
              <Text variant="h3">Welcome to the API Explorer!</Text>
              <Badge
                title="New"
                size="medium"
                color="green"
                icon="autoFixHigh"
              />
            </Box>
            <Text variant="normal" color="grey11">
              Explore your Prismic content, run queries to see your data in
              real-time, and learn how to use the Prismic API with{" "}
              <Text href={prismicClientDocsUrl} underline color="grey11">
                @prismicio/client
              </Text>
              .
            </Text>
            {
              /* We don't display the button is the repository is already set (i.e. running in a Prismic repository) */
              !config.repository && (
                <Button
                  sx={{ marginTop: 16 }}
                  onClick={runDemoQuery}
                  size="large"
                  startIcon="playCircle"
                >
                  Run Demo Query
                </Button>
              )
            }
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

function APIExplorerResponsePending() {
  return (
    <Box display="grid" gridTemplateRows="auto 1fr" padding={16}>
      <Alert color="info" icon="terminal" title="Fetching data" titleLoading />
    </Box>
  )
}

type APIExplorerResponseErrorProps = {
  response: ResponseError
}

function APIExplorerResponseError(props: APIExplorerResponseErrorProps) {
  const { response } = props

  return (
    <Box display="grid" gridTemplateRows="auto 1fr" padding={16}>
      <Alert
        color="error"
        icon="alert"
        title={
          response.error instanceof MissingParameterError
            ? "Waiting for parameters..."
            : "Error fetching data"
        }
        subtitle={response.error.message}
      />
    </Box>
  )
}

type APIExplorerResponseSuccessProps = {
  response: ResponseSuccess
}

function APIExplorerResponseSuccess(props: APIExplorerResponseSuccessProps) {
  const { response } = props

  return (
    <Box display="grid" gridTemplateRows="auto 1fr" minHeight={0}>
      <ResponseSuccessHeader response={response} />
      <ResponseSuccessPreview response={response} />
    </Box>
  )
}

type ResponseSuccessHeaderProps = APIExplorerResponseSuccessProps

function ResponseSuccessHeader(props: ResponseSuccessHeaderProps) {
  const { response } = props

  let resultSize = null
  if ("total_results_size" in response.data) {
    resultSize = response.data.total_results_size
  } else if (Array.isArray(response.data)) {
    resultSize = response.data.length
  }

  const { copy: copyRef, isCopied: isRefCopied } = useCopy()

  return (
    <Box
      as="header"
      padding={{ inline: 16 }}
      alignItems="center"
      border={{ bottom: true }}
      minHeight={48}
    >
      <Box flexGrow={1} gap={12}>
        {!!response.ref && (
          <Badge
            icon="tag"
            title={
              <>
                <Text variant="small" color="grey11">
                  ref: {response.ref}
                </Text>
                <Icon
                  name={isRefCopied ? "check" : "contentCopy"}
                  size="extraSmall"
                  color="grey11"
                />
              </>
            }
            size="medium"
            color="grey"
            onClick={() => copyRef(response.ref ?? "")}
          />
        )}
      </Box>
      <Box gap={12}>
        {!!resultSize && (
          <>
            <Box alignItems="center" gap={4}>
              <Icon name="insertDriveFile" size="extraSmall" color="grey11" />
              <Text variant="small" color="grey11" component="p">
                {pluralize(resultSize, "Document", "Documents")}{" "}
                <Text variant="small" color="purple9" component="span">
                  {resultSize}
                </Text>
              </Text>
            </Box>
          </>
        )}
        {!!response.duration && (
          <>
            <Box alignItems="center" gap={4}>
              <Icon name="schedule" size="extraSmall" color="grey11" />
              <Text variant="small" color="grey11" component="p">
                Time{" "}
                <Text variant="small" color="green9" component="span">
                  {response.duration.toString()}ms
                </Text>
              </Text>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

type ResponseSuccessPreviewProps = APIExplorerResponseSuccessProps

function ResponseSuccessPreview(props: ResponseSuccessPreviewProps) {
  const { response } = props
  const { request } = useAPIExplorer()

  const { copy: copyResponse, isCopied: isResponseCopied } = useCopy()

  const collapse = useMemo(() => {
    // getAll* requests can get really large, so we collapse them more strictly.
    if (Array.isArray(response.data) && request.type.startsWith("getAll")) {
      return response.data.length > 100 ? 1 : 2
    }

    return 3
  }, [request.type, response.data])

  // We're using the JSON Editor components with lots of style choices/patches here for now.
  // Once we'll be more satisfied with its looks and feel, as well as the final library used
  // (if any), we'll be able to move it to `@prismicio/editor-ui` and document it for reuse.
  return (
    <Box
      display="flex"
      flexDirection="column"
      overflowY="auto"
      backgroundColor="white"
    >
      {/* We need to set the `z-index`to `0` so that sticky/absolute elements don't overlap dialogs */}
      <div style={{ zIndex: 0 }}>
        {/* Position sticky element needs to be first in the HTML tree */}
        {/* and requires a `z-index` value to remain clickable. */}
        <div style={{ position: "sticky", top: 0, left: 0, zIndex: 1 }}>
          <Box position="absolute" top={16} right={16} gap={8}>
            <Button
              startIcon={isResponseCopied ? "check" : "contentCopy"}
              color="grey"
              onClick={() =>
                copyResponse(JSON.stringify(response.data, null, 2))
              }
            >
              Copy
            </Button>
            {response.url && (
              <Button endIcon="link" asChild color="grey">
                <a
                  href={response.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Open CDN response in a new tab"
                ></a>
              </Button>
            )}
          </Box>
        </div>
        <JsonEditor
          className="responsePreview"
          data={response.data}
          enableClipboard={false}
          restrictEdit
          restrictDelete
          restrictAdd
          theme={[
            {
              styles: {
                container: {
                  backgroundColor: "transparent",
                  fontFamily:
                    'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                },
                // Colors from GitHub's theme
                property: "#0550ae",
                bracket: { color: "#1f2328", fontWeight: "normal" },
                string: "##0a3069",
                number: "#0550ae",
                boolean: "#0550ae",
                null: {
                  color: "#0550ae",
                  fontVariant: "initial",
                  fontWeight: "normal",
                },
              },
            },
          ]}
          icons={{ chevron: <Icon name="expandMore" size="small" /> }}
          indent={3}
          collapse={collapse}
          collapseAnimationTime={0}
          rootName=""
          showArrayIndices={false}
          showStringQuotes
          showCollectionCount="when-closed"
          stringTruncate={Infinity}
          minWidth="100%"
          maxWidth="auto"
          rootFontSize="calc(14rem / 16)"
        />
        <style>{
          /* css */ `
          .responsePreview {
            /* Fixing weird text alignment */
            & .jer-value-main-row {
              min-height: 1.5em;
            }
            & .jer-key-text:has(+ div .jer-value-string) {
              align-self: baseline;
              line-height: 1.5em;
            }
            & .jer-value-string {
              line-height: 1.5em;
            }
            & .jer-value-and-buttons:has(.jer-value-string) {
              align-self: baseline;
            }
            & .jer-brackets::before {
              content: " ";
            }
            & .jer-rotate-90 {
              transform: rotate(-90deg) translateX(.1em);
            }

            & .jer-input-component [class*=jer-value]::after,
            & .jer-bracket-outside::after,
            & .jer-brackets.jer-visible::after {
              content: ",";
              color: #1f2328;
            }

            & .jer-bracket-outside::before {
              white-space: pre;
            }

            /* Removing awkward UX */
            & .jer-clickzone {
              display: none;
            }

            & .jer-collapse-icon {
              left: -1.4rem;
              cursor: pointer;
            }
          }
        `
        }</style>
      </div>
    </Box>
  )
}
